
<template>
  <div>
    <h1 style="margin-top:0">
      Admin: {{ org.name }}
      <small
        v-if="org.orgExpirationDate"
        style="font-size: 10pt;"
      >
        (Access Expires: {{ org.orgExpirationDate | dateShort }})
      </small>
    </h1>
    <div v-if="loaded">
      <h2
        id="admin-nav"
        class="sr-only"
      >
        Admin Navigation
      </h2>
      <nav aria-labelledby="admin-nav">
        <ul
          class="nav nav-tabs admin-tabs"
          aria-label="admin page navigation"
        >
          <li
            :class="{ active: (activeTab === 'customer-admin-groups' || activeTab === 'customer-admin-groups-groupId' || activeTab === 'customer-admin-users-groupId') }"
          >
            <a
              id="customer-admin-groupslink"
              ref="customer-admin-groupslink"
              href="#"
              @click.prevent="goTo('customer-admin-groups')"
              @keypress="goTo('customer-admin-groups', $event)"
            >
              Manage {{ isEdu ? 'Classes' : 'Groups' }}
            </a>
          </li>
          <li
            :class="{ active: activeTab === 'customer-admin-invites' }"
          >
            <a
              id="customer-admin-inviteslink"
              ref="customer-admin-inviteslink"
              href="#"
              @click.prevent="goTo('customer-admin-invites')"
              @keypress="goTo('customer-admin-invites', $event)"
            >
              {{ isEdu ? 'Add Instructor' : 'Add Coach' }}
            </a>
          </li>
          <li
            :class="{ active: (activeTab === 'customer-admin-users' || activeTab === 'customer-admin-users-userId'
              || activeTab === 'customer-admin-users-userId-groupId') }"
          >
            <a
              id="customer-admin-userslink"
              ref="customer-admin-userslink"
              href="#"
              @click.prevent="goTo('customer-admin-users')"
              @keypress="goTo('customer-admin-users', $event)"
            >
              Manage Users
            </a>
          </li>
          <li
            :class="{ active: activeTab === 'customer-admin-pending-invites' }"
          >
            <a
              id="customer-admin-pending-inviteslink"
              ref="customer-admin-pending-inviteslink"
              href="#"
              @click.prevent="goTo('customer-admin-pending-invites')"
              @keypress="goTo('customer-admin-pending-invites', $event)"
            >
              Pending Invites
            </a>
          </li>
        </ul>
      </nav>
      <div
        v-show="activeTab === 'customer-admin-pending-invites'"
        class="tab-well"
      >
        <keep-alive>
          <pending-invites
            :org="org"
            :is-edu="isEdu"
            :groups="groups"
            :group-ids-to-names="groupIdsToNames"
            :invites="invites"
          />
        </keep-alive>
      </div>
      <div
        v-show="activeTab === 'customer-admin-invites'"
        class="tab-well"
      >
        <manager-invite-form
          :org="org"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
        />
      </div>
      <div
        v-show="activeTab === 'customer-admin-groups'"
        class="tab-well"
      >
        <admin-manage-groups
          :hide-privacy="hidePrivacy"
          :users="users"
          :org="org"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
        />
      </div>
      <div
        v-show="activeTab === 'customer-admin-groups-groupId'"
        class="tab-well"
      >
        <admin-edit-group
          :hide-privacy="hidePrivacy"
          :group="group"
          :org="org"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
        />
      </div>
      <div
        v-show="activeTab === 'customer-admin-users-groupId'"
        class="tab-well"
      >
        <admin-manage-users
          :users="groupUsers"
          :org="org"
          :group="group"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
        />
      </div>
      <div
        v-show="activeTab === 'customer-admin-users-userId' || activeTab === 'customer-admin-users-userId-groupId'"
        class="tab-well"
      >
        <admin-manage-user
          :user="user"
          :org="org"
          :group="group"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
          @userUpdate="userUpdate"
        />
      </div>
      <div
        v-show="activeTab === 'customer-admin-users'"
        class="tab-well"
      >
        <admin-search-users
          :users="users"
          :org="org"
          :is-edu="isEdu"
          :groups="groups"
          :group-ids-to-names="groupIdsToNames"
        />
      </div>
    </div>
    <div v-else>
      <h2
        style="text-align: center;"
      >
        Loading...
      </h2>
    </div>
  </div>
</template>

<style lang="less">
  .admin-tabs li {
    @media (max-width: 600px) {
      width: 84px;
      a {
        font-size: 8px;
        padding-left: 4px;
      }
    }
    @media (min-width: 601px) {
      width: 130px;
    }
  }
</style>

<script>
import ManagerInviteForm from '../forms/ManagerInviteForm';
import PendingInvites from '../layout/PendingInvites';
import AdminManageGroups from '../layout/AdminManageGroups';
import AdminEditGroup from '../layout/AdminEditGroup';
import AdminManageUsers from '../layout/AdminManageUsers';
import AdminManageUser from '../layout/AdminManageUser';
import AdminSearchUsers from '../layout/AdminSearchUsers';
export default {
  components: { ManagerInviteForm, PendingInvites, AdminManageGroups, AdminManageUsers,
    AdminManageUser, AdminEditGroup, AdminSearchUsers },
  data() {
    return {
      activeTab: null,
      loaded: false,
      isEdu: false,
      expirationAlreadyShown: false,
    };
  },
  computed: {
    showOrgExpiration() {
      if (this.expirationAlreadyShown) {
        return false;
      }
      this.expirationAlreadyShown = true;

      const in45Days = this.moment().add(45, 'days');
      return (this.org.orgExpirationDate && this.moment(this.org.orgExpirationDate).isBefore(in45Days));
    },
    isOrgLevelAdmin() {
      return (this.$store.getters.org && this.$store.getters.org.roles && this.$store.getters.org.roles.indexOf('admin') !== -1);
    },
    groups() {
      // this includes groups user might not belong to whereas getters.activeGroups does not.
      return this._.sortBy(
        this.$store.getters.groups, g => -new Date(g.createdDate).getTime()
      );
    },
    group: {
      cache: false,
      get() {
        const groupId = this.$router.currentRoute.params.groupId;

        if (groupId === '00000000-0000-4000-0000-000000000000') {
          return {
            groupId: '00000000-0000-4000-0000-000000000000',
            name: (this.isEdu) ? 'Instructors and Admins' : 'Coaches and Admins',
          };
        }
        if (groupId === '0' || groupId === 0) {
          return {
            groupId: 0,
            name: '',
          };
        }
        return this._.find(this.groups, {groupId});
      }
    },
    hidePrivacy() {
      const flag = 'Hide_Privacy_Levels_In_Admin';
      const settings = this.$store.getters.org.settings;
      return !!(flag in settings && settings[flag]);
    },
    groupUsers: {
      cache: false,
      get() {
        const groupId = this.$router.currentRoute.params.groupId;
        return this.users[groupId] ?
          _.sortBy(this.users[groupId], [(u) => u.lastName.toLowerCase(), (u) => u.firstName.toLowerCase()]) :
          [];
      }
    },
    org: {
      cache: false,
      get() {
        // This may be undefined when the page first loads
        if (!this.$store.getters.org) {
          return {name: '', hasPerpetualFreeDuration: false};
        }
        const org = this._.clone(this.$store.getters.org);
        org.hasPerpetualFreeDuration =
          (org.settings && org.settings.freeDuration && org.settings.freeDuration.years && org.settings.freeDuration.years >= 15);
        return org;
      }
    },
    user: {
      cache: false,
      get() {
        const userId = window.parseInt(this.$router.currentRoute.params.userId, 10);
        const members = this.users;
        const user = {
          groups: {}
        };

        this._.forEach(members, (groupUsers, groupId) => {
          this._.forEach(groupUsers, (_user) => {
            if (_user.userId === userId) {
              user.groups[groupId] = _user;
              user.groups[groupId].groupId = groupId;
              user.groups[groupId].groupName = this.groupIdsToNames[groupId];

              ['email', 'name', 'userId'].forEach((field) => {
                user[field] = _user[field];
              });
            }
          });
        });
        return user;
      }
    },
    /**
       * This collection is super hacky. It has some weird characteristics:
       *   - The '00000000-0000-4000-0000-000000000000' group includes both
       *     orgLevel Admin/Managers AND regular managers
       *   - orgLevel Admin/Managers are identified by the isOrgManager/isOrgAdmin
       *     attributes
       *   - The "roles" list in the manager group was removed since "manager" in there
       *     could refer to either the Org or a random group
       */
    users: {
      cache: false,
      get() {
        let _users = this.$store.getters.groupMembers;
        let users = this._.clone(_users);

        let managers = {};
        // First, populate the orgLevel Managers/Admins
        this._.forEach(users['00000000-0000-4000-0000-000000000000'], (user) => {
          if (user.roles && user.roles.indexOf('manager') !== -1 && user.membershipStatus === 'active') {
            user.isOrgManager = true;
          }
          if (user.roles && user.roles.indexOf('admin') !== -1 && user.membershipStatus === 'active') {
            user.isOrgAdmin = true;
          }
          user.groupId = '00000000-0000-4000-0000-000000000000';
          delete user.roles;
          managers[user.userId] = user;
        });

        this._.forEach(users, (group, groupId) => {
          this._.forEach(group, (user) => {
            if (user.roles && user.roles.indexOf('manager') !== -1 && !managers[user.userId]) {
              managers[user.userId] = this._.clone(user);
              managers[user.userId].groupId = '00000000-0000-4000-0000-000000000000';
              delete managers[user.userId].roles;
              user.isOrgAdmin = false;
              user.isOrgManager = false;
            } else {
              user.groupId = groupId;
            }
          });
        });
        users['00000000-0000-4000-0000-000000000000'] = managers;
        return users;
      }
    },
    invites() {
      // this getter augments the invites so they are guaranteed to have an explicit
      // org-level role (even if false.)
      return this.$store.getters.invitesWithOrgLevelRole;
    },
    groupIdsToNames() {
      let groupNames = {};
      this._.forEach(this.groups, g => {
        groupNames[g.groupId] = g.name;
      });
      groupNames[null] = 'Additional permissions';

      return groupNames;
    }
  },
  watch: {
    '$route' () {
      this.activeTab = this.$router.currentRoute.name;
      if (this.$router.currentRoute.name === 'customer-admin-groups') {
        // Always reload data on Group summary page
        this.loadData(() => this.$forceUpdate());
      } else {
        this.$forceUpdate();
      }
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    userUpdate() {
      this.$forceUpdate();
    },
    goTo(name, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      const link = this.$refs[name.replace(/ /g, '') + 'link'];
      link.blur();
      this.$nextTick(() => {
        const header = document.getElementById(name.replace(/ /g, '') + 'header');
        if (header) {
          header.focus();
        }
      });

      this.activeTab = name;
      this.$router.push({ name });
    },
    showExpirationNotice() {
      if (!this.showOrgExpiration) return true;

      const expiration = this.$options.filters.dateShort(this.org.orgExpirationDate);
      this.$store.dispatch('notify', {
        type: 'warning',
        subject: 'Your Invoice Date is Approaching',
        text: `Your organization is due to expire on
          ${expiration}. Please contact
          <a href="mailto:support@bigtincan.com">support@bigtincan.com</a>
          to renew.<br><br>`
      });
    },
    loadData(callback=() => {}) {
      const promises1 = [
        this.$store.dispatch('getInvites'),
        this.$store.dispatch('getOrg'),
        this.$store.dispatch('listGroups'),
      ];
      window.Promise.all(promises1).then(() => {
        const promises2 = [
          this.$store.dispatch('listGroupMembers', true),
        ];
        window.Promise.all(promises2).then(() => {
          this.activeTab = this.$router.currentRoute.name;
          this.isEdu = this.$store.getters.org && this.$store.getters.org.type == 'edu';
          this.showExpirationNotice();

          if (!this.isOrgLevelAdmin) {
            return this.$router.push({ name: 'subscription' });
          }

          this.loaded = true;
          callback();
        });
      });
    }
  }
};
</script>
